import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseFectch';

export const integrationApi = createApi({
  reducerPath: 'integrations',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    updateSlackInfo: builder.mutation({
      query: ({ data, companyId }) => ({
        url: `/slack/${companyId}`,
        method: 'PUT',
        body: data,
      }),
    }),
    getTeams: builder.query({
      query: (companyId) => `/teams/teams/${companyId}`,
    }),
    getTeamsChannels: builder.query({
      query: ({ companyId, teamId }) =>
        `/teams/${companyId}/channels/${teamId}`,
    }),
    updateTeamsInfo: builder.mutation({
      query: ({ data, companyId }) => ({
        url: `/teams/${companyId}`,
        method: 'PUT',
        body: data,
      }),
    }),
    disconnectTeams: builder.mutation({
      query: (companyId) => ({
        url: `/teams/${companyId}`,
        method: 'DELETE',
      }),
    }),
    getTeamsConnectionStatus: builder.query({
      query: (companyId) => `/teams/status/${companyId}`,
    }),
    getDiscordGuilds: builder.query({
      query: (companyId) => `/discord/guilds/${companyId}`,
    }),
    getDiscordChannels: builder.query({
      query: ({ companyId, guildId }) =>
        `/discord/${companyId}/channels/${guildId}`,
    }),
    updateDiscordInfo: builder.mutation({
      query: ({ data, companyId }) => ({
        url: `/discord/${companyId}`,
        method: 'PUT',
        body: data,
      }),
    }),
    disconnectDiscord: builder.mutation({
      query: (companyId) => ({
        url: `/discord/${companyId}`,
        method: 'DELETE',
      }),
    }),
    getDiscordConnectionStatus: builder.query({
      query: (companyId) => `/discord/status/${companyId}`,
    }),
    updateTelegramInfo: builder.mutation({
      query: ({ data, companyId }) => ({
        url: `/telegram/${companyId}`,
        method: 'PUT',
        body: data,
      }),
    }),
    disconnectTelegram: builder.mutation({
      query: (companyId) => ({
        url: `/telegram/${companyId}`,
        method: 'DELETE',
      }),
    }),
    getTelegramConnectionStatus: builder.query({
      query: (companyId) => `/telegram/status/${companyId}`,
    }),
    getTrelloBoards: builder.query({
      query: (companyId) => `/trello/boards/${companyId}`,
    }),
    getTrelloLists: builder.query({
      query: ({ companyId, boardId }) =>
        `/trello/${companyId}/lists/${boardId}`,
    }),
    updateTrelloInfo: builder.mutation({
      query: ({ data, companyId }) => ({
        url: `/trello/${companyId}`,
        method: 'PUT',
        body: data,
      }),
    }),
    disconnectTrello: builder.mutation({
      query: (companyId) => ({
        url: `/trello/${companyId}`,
        method: 'DELETE',
      }),
    }),
    getTrelloConnectionStatus: builder.query({
      query: (companyId) => `/trello/status/${companyId}`,
    }),
    getAirtableBases: builder.query({
      query: (companyId) => `/airtable/bases/${companyId}`,
    }),
    getAirtableTables: builder.query({
      query: ({ companyId, baseId }) =>
        `/airtable/${companyId}/tables/${baseId}`,
    }),
    updateAirtableInfo: builder.mutation({
      query: ({ data, companyId }) => ({
        url: `/airtable/${companyId}`,
        method: 'PUT',
        body: data,
      }),
    }),
    disconnectAirtable: builder.mutation({
      query: (companyId) => ({
        url: `/airtable/${companyId}`,
        method: 'DELETE',
      }),
    }),
    getAirtableConnectionStatus: builder.query({
      query: (companyId) => `/airtable/status/${companyId}`,
    }),
    getEventsByCompany: builder.query({
      query: ({ companyId, countryCode, year }) =>
        `/events/${companyId}?countryCode=${countryCode}&year=${year}`,
    }),
    createEvent: builder.mutation({
      query: (data) => {
        return {
          url: `/events`,
          method: 'POST',
          body: data,
        };
      },
    }),
    addEmployeesFromSheet: builder.mutation({
      query: ({ companyId, file }) => {
        const formData = new FormData();
        formData.append('employees', file);
        return {
          url: `/google/sheet/${companyId}`,
          method: 'POST',
          body: formData,
        };
      },
    }),
    updateAndVerifyGoogleSheet: builder.mutation({
      query: (data) => {
        return {
          url: `/google/update/sheet`,
          method: 'POST',
          body: data,
        };
      },
    }),

    updateEvent: builder.mutation({
      query: ({ eventId, data }) => ({
        url: `/events/${eventId}`,
        method: 'PUT',
        body: data,
      }),
    }),
    uploadBulkEvents: builder.mutation({
      query: (data) => ({
        url: `/events/bulk`,
        method: 'POST',
        body: data,
      }),
    }),
    getChannels: builder.query({
      query: (teamId) => `/slack/channels?team_id=${teamId}`,
    }),
    deleteEvent: builder.mutation({
      query: (eventId) => ({
        url: `/events/${eventId}`,
        method: 'DELETE',
      }),
    }),

    validateCompanyFields: builder.query({
      query: ({ email, name }) => {
        const params = new URLSearchParams();

        if (email) params.append('email', email);
        if (name) params.append('name', name);

        return `/company/validate?${params.toString()}`;
      },
    }),
    uploadResumes: builder.mutation({
      query: ({ jobId, data }) => {
        return {
          url: `/job/resumes/${jobId}`,
          method: 'POST',
          body: data,
        };
      },
    }),
    addJob: builder.mutation({
      query: (jobData) => ({
        url: '/job',
        method: 'POST',
        body: jobData,
      }),
    }),
    getJobsByCompany: builder.query({
      query: (companyId) => `/jobs/${companyId}`,
    }),
    getCandidateById: builder.query({
      query: (candidateId) => `/candidate/${candidateId}`,
    }),
    getResumesByCompanyId: builder.query({
      query: (companyId) => `/resumes/${companyId}`,
    }),
    updateGoogleFolderId: builder.mutation({
      query: ({ companyId, data }) => ({
        url: `/google/drive/folder/${companyId}`,
        method: 'PUT',
        body: data,
      }),
    }),
    updateCandidate: builder.mutation({
      query: (candidateData) => ({
        url: '/candidate',
        method: 'PUT',
        body: candidateData,
      }),
    }),
    updateMultipleCandidates: builder.mutation({
      query: (candidateData) => ({
        url: '/candidates',
        method: 'PUT',
        body: candidateData,
      }),
    }),
    getGoogleDriveConnectionStatus: builder.query({
      query: (companyId) => `/google/drive/status/${companyId}`,
    }),
    getGoogleDriveFolders: builder.query({
      query: (companyId) => `/google/drive/folders/${companyId}`,
    }),
    getDocumentsByCompanyId: builder.query({
      query: (companyId) => `/documents/${companyId}`,
    }),

    deleteJob: builder.mutation({
      query: (jobId) => ({
        url: `/jobs/${jobId}`,
        method: 'DELETE',
      }),
    }),
    updateJob: builder.mutation({
      query: ({ jobId, data }) => ({
        url: `/jobs/${jobId}`,
        method: 'PUT',
        body: data,
      }),
    }),

    getQuestionsByCompanyId: builder.query({
      query: (companyId) => `/questions/${companyId}`,
    }),
    getCandidatesByCompanyId: builder.query({
      query: (companyId) => `/candidates/${companyId}`,
    }),
    getJobById: builder.query({
      query: (jobId) => `/job/${jobId}`,
    }),
  }),
});

export const {
  useAddEmployeesFromSheetMutation,
  useUpdateSlackInfoMutation,
  useUpdateAndVerifyGoogleSheetMutation,
  useLazyGetEventsByCompanyQuery,
  useCreateEventMutation,
  useLazyValidateCompanyFieldsQuery,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useLazyGetChannelsQuery,
  useUploadBulkEventsMutation,
  useUploadResumesMutation,
  useAddJobMutation,
  useLazyGetJobsByCompanyQuery,
  useGetJobByIdQuery,
  useLazyGetResumesByCompanyIdQuery,
  useLazyGetCandidatesByCompanyIdQuery,
  useLazyGetCandidateByIdQuery,
  useUpdateCandidateMutation,
  useLazyGetDocumentsByCompanyIdQuery,
  useLazyGetQuestionsByCompanyIdQuery,
  useLazyGetGoogleDriveConnectionStatusQuery,
  useLazyGetGoogleDriveFoldersQuery,
  useUpdateGoogleFolderIdMutation,
  useUpdateMultipleCandidatesMutation,
  useDeleteJobMutation,
  useUpdateJobMutation,
  useLazyGetTeamsQuery,
  useLazyGetTeamsChannelsQuery,
  useUpdateTeamsInfoMutation,
  useDisconnectTeamsMutation,
  useLazyGetTeamsConnectionStatusQuery,
  useLazyGetDiscordGuildsQuery,
  useLazyGetDiscordChannelsQuery,
  useUpdateDiscordInfoMutation,
  useDisconnectDiscordMutation,
  useLazyGetDiscordConnectionStatusQuery,
  useUpdateTelegramInfoMutation,
  useDisconnectTelegramMutation,
  useLazyGetTelegramConnectionStatusQuery,
  useLazyGetTrelloBoardsQuery,
  useLazyGetTrelloListsQuery,
  useUpdateTrelloInfoMutation,
  useDisconnectTrelloMutation,
  useLazyGetTrelloConnectionStatusQuery,
  useLazyGetAirtableBasesQuery,
  useLazyGetAirtableTablesQuery,
  useUpdateAirtableInfoMutation,
  useDisconnectAirtableMutation,
  useLazyGetAirtableConnectionStatusQuery,
} = integrationApi;
